"use client";

import { useState, useEffect } from "react";
import { createClient } from "@/lib/supabase";
import { extractUsername, syncGitHubToDb } from "@/lib/github";
import type { GitHubCache } from "@/types/database";

const SYNC_INTERVAL = 60 * 60 * 1000; // 1 hour

export function useGitHubStats(githubUrl: string | null | undefined, token?: string | null) {
    const [data, setData] = useState<GitHubCache | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!githubUrl) return;
        const username = extractUsername(githubUrl);
        if (!username) return;

        async function fetchStats() {
            setLoading(true);
            const supabase = createClient();

            try {
                // 1. Try to get from DB
                const { data: cached, error: dbError } = await supabase
                    .from("github_cache")
                    .select("*")
                    .eq("username", username)
                    .maybeSingle();

                if (dbError) throw dbError;

                const isStale = !cached || (Date.now() - new Date(cached.fetched_at).getTime() > SYNC_INTERVAL);

                if (isStale && username) {
                    // 2. If missing or stale, sync from GitHub
                    const synced = await syncGitHubToDb(username, token);
                    if (synced) {
                        setData(synced);
                    } else if (cached) {
                        // Fallback to stale data if sync fails
                        setData(cached);
                    }
                } else {
                    // 3. Otherwise use cached
                    setData(cached);
                }
            } catch (err: any) {
                console.warn("useGitHubStats error:", err);
                const message =
                    typeof err === "string"
                        ? err
                        : err?.message || "Failed to load GitHub stats";
                setError(message);
            } finally {
                setLoading(false);
            }
        }

        fetchStats();
    }, [githubUrl, token]);

    return { data, loading, error };
}
